/**
 * PaymentsSetupChecklist (Mode A)
 *
 * Shown on the Lease Detail Payments tab while `paymentMethod === 'none'` (payments not yet
 * enabled). A linear, one-step-open-at-a-time checklist over the four one-time setup steps
 * (method -> deposit/monitoring account -> terms -> late fees). Each step hosts the EXISTING
 * editor component unmodified (PaymentMethodCard / CollectionSetupCard / PaymentTermsCard /
 * LateFeeConfigSection) — this component only supplies the accordion shell + collapsed-state
 * summaries, per the change-19 design (state-aware Payments tab).
 *
 * The "Activate Payments" affordance (PaymentActivationCard) is not one of the four numbered
 * steps, but is still surfaced beneath them whenever a method has been chosen — matching its
 * current always-visible-once-a-method-is-selected behavior and giving the `?focus=activate`
 * deep-link (AC-8) a target in Mode A too.
 */

import { useEffect, useState } from 'react';
import { ChevronRight, Check } from 'lucide-react';
import {
  type PaymentConfig,
  type UpdatePaymentConfigInput,
  type LandlordFundingSource,
  type PropertyBankInfo,
  type PaymentReadiness,
} from '../../lib/api';
import type { LinkedBankAccount } from '../../api/linked-bank-accounts.api';
import type { MethodSelection } from './payment-helpers';
import { PaymentMethodCard, CollectionSetupCard, PaymentTermsCard, PaymentActivationCard } from './PaymentSettingsForm';
import { LateFeeConfigSection } from './LateFeeConfigSection';

type StepKey = 'method' | 'account' | 'terms' | 'late-fees';

const STEP_ORDER: StepKey[] = ['method', 'account', 'terms', 'late-fees'];

// Maps the ?focus= deep-link values (shared with Mode B / PaymentsStatusPanel) onto this
// checklist's step keys. 'activate' has no step target here — it targets the
// PaymentActivationCard rendered below the checklist instead.
const FOCUS_TO_STEP: Record<string, StepKey> = {
  deposit: 'account',
  monitoring: 'account',
  terms: 'terms',
  'late-fees': 'late-fees',
};

interface PaymentsSetupChecklistProps {
  paymentConfig: PaymentConfig | null;
  selectedMethod: MethodSelection;
  readiness: PaymentReadiness;
  fundingSources: LandlordFundingSource[];
  propertyBankInfo: PropertyBankInfo | null;
  linkedBankAccounts: LinkedBankAccount[];
  onUpdateConfig: (data: UpdatePaymentConfigInput) => Promise<boolean>;
  onMethodChange: (method: MethodSelection) => void;
  onSetPropertyDefault: (fundingSourceId: string | null) => void;
  onSetMonitoringAccount: (linkedBankAccountId: string) => void;
  /** ?focus= deep-link value (deposit | terms | late-fees | monitoring | activate). */
  focusSection?: string | null;
  /** Called once the deep-link target has been expanded, so the page can strip ?focus=. */
}

export function PaymentsSetupChecklist({
  paymentConfig,
  selectedMethod,
  readiness,
  fundingSources,
  propertyBankInfo,
  linkedBankAccounts,
  onUpdateConfig,
  onMethodChange,
  onSetPropertyDefault,
  onSetMonitoringAccount,
  focusSection,
}: PaymentsSetupChecklistProps) {
  const wantsAch = selectedMethod === 'ach';
  const wantsExternal = selectedMethod === 'external';

  const methodDone = selectedMethod !== 'none';
  const accountDone = wantsAch ? readiness.ach.ready : wantsExternal ? readiness.external.ready : false;
  const termsDone = paymentConfig?.termsSetAt != null;
  const lateFeesDone = paymentConfig?.lateFeesSetAt != null;

  const completion: Record<StepKey, boolean> = {
    method: methodDone,
    account: accountDone,
    terms: termsDone,
    'late-fees': lateFeesDone,
  };

  const accountIssues = wantsAch ? readiness.ach.issues : wantsExternal ? readiness.external.issues : [];
  const accountNeedsAttention = accountIssues.some((i) => i.severity === 'fatal');

  const [manualStep, setManualStep] = useState<StepKey | null>(null);
  const focusStep = focusSection ? FOCUS_TO_STEP[focusSection] : undefined;

  // A focus deep-link always wins the step-open decision the moment it arrives, and stays the
  // chosen step (via manualStep) once the URL param is stripped — otherwise the step would
  // collapse back to the computed default right as onFocusConsumed clears ?focus=.
  useEffect(() => {
    if (focusStep) setManualStep(focusStep);
  }, [focusStep]);

  const defaultStep = STEP_ORDER.find((k) => !completion[k]) ?? 'late-fees';
  let expandedStep: StepKey = focusStep ?? manualStep ?? defaultStep;
  // Steps 2-4 are gated until a method is chosen — never leave one of them expanded.
  if (selectedMethod === 'none') expandedStep = 'method';

  function handleStepClick(step: StepKey) {
    if (step !== 'method' && selectedMethod === 'none') return;
    setManualStep(step);
  }

  const accountLabel = wantsAch ? 'Deposit account' : wantsExternal ? 'Monitoring account' : 'Deposit / monitoring account';
  const accountSummary = describeAccount(wantsAch, wantsExternal, paymentConfig, propertyBankInfo, fundingSources);
  const methodSummary = describeMethod(selectedMethod);
  const termsSummary = termsDone && paymentConfig
    ? `Due ${paymentConfig.rentDueDay}${getDaySuffix(paymentConfig.rentDueDay)} · ${paymentConfig.gracePeriodDays}-day grace`
    : 'Not set';
  const lateFeeSummary = lateFeesDone ? describeLateFee(paymentConfig) : 'Not set';

  const stepNumber = STEP_ORDER.indexOf(expandedStep) + 1;

  return (
    <div data-testid="payments-setup-checklist" className="flex flex-col gap-3">
      <div className="px-1">
        <h3 className="text-lg font-semibold text-foreground m-0">Set up rent collection</h3>
        <p className="text-sm text-muted-foreground mt-0.5 mb-0">Step {stepNumber} of 4</p>
      </div>

      <StepRow
        stepKey="method"
        stepIndex={1}
        title="Payment method"
        summary={methodSummary}
        disabled={false}
        complete={methodDone}
        needsAttention={false}
        expanded={expandedStep === 'method'}
        onToggle={() => handleStepClick('method')}
      >
        <PaymentMethodCard selectedMethod={selectedMethod} onMethodChange={onMethodChange} />
      </StepRow>

      <StepRow
        stepKey="account"
        stepIndex={2}
        title={accountLabel}
        summary={accountSummary}
        disabled={selectedMethod === 'none'}
        complete={accountDone}
        needsAttention={accountNeedsAttention}
        expanded={expandedStep === 'account'}
        onToggle={() => handleStepClick('account')}
      >
        <CollectionSetupCard
          paymentConfig={paymentConfig}
          selectedMethod={selectedMethod}
          readiness={readiness}
          fundingSources={fundingSources}
          propertyBankInfo={propertyBankInfo}
          linkedBankAccounts={linkedBankAccounts}
          onUpdate={onUpdateConfig}
          onSetPropertyDefault={onSetPropertyDefault}
          onSetMonitoringAccount={onSetMonitoringAccount}
        />
      </StepRow>

      <StepRow
        stepKey="terms"
        stepIndex={3}
        title="Payment terms"
        summary={termsSummary}
        disabled={selectedMethod === 'none'}
        complete={termsDone}
        needsAttention={false}
        expanded={expandedStep === 'terms'}
        onToggle={() => handleStepClick('terms')}
      >
        <PaymentTermsCard paymentConfig={paymentConfig} onUpdate={onUpdateConfig} />
      </StepRow>

      <StepRow
        stepKey="late-fees"
        stepIndex={4}
        title="Late fees"
        summary={lateFeeSummary}
        disabled={selectedMethod === 'none'}
        complete={lateFeesDone}
        needsAttention={false}
        expanded={expandedStep === 'late-fees'}
        onToggle={() => handleStepClick('late-fees')}
      >
        <LateFeeConfigSection paymentConfig={paymentConfig} onUpdate={onUpdateConfig} />
      </StepRow>

      {/* Not one of the four numbered steps — the "go live" action, surfaced once a method is
          chosen so the checklist can lead into an active (Mode B) lease. Reused unmodified;
          it already renders its own readiness-blocked state when steps above aren't done. */}
      {selectedMethod !== 'none' && (
        <PaymentActivationCard
          paymentConfig={paymentConfig}
          selectedMethod={selectedMethod}
          readiness={readiness}
          onUpdate={onUpdateConfig}
        />
      )}
    </div>
  );
}

interface StepRowProps {
  stepKey: StepKey;
  stepIndex: number;
  title: string;
  summary: string;
  disabled: boolean;
  complete: boolean;
  needsAttention: boolean;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function StepRow({ stepKey, stepIndex, title, summary, disabled, complete, needsAttention, expanded, onToggle, children }: StepRowProps) {
  return (
    <div
      data-testid={`payments-setup-step-${stepKey}`}
      className={`rounded-lg border overflow-hidden transition-colors ${
        expanded ? 'border-primary' : 'border-border'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-expanded={expanded}
        aria-disabled={disabled}
        data-testid={`payments-setup-step-${stepKey}-toggle`}
        className={`w-full flex items-center justify-between gap-3 p-4 min-h-[44px] text-left transition-colors ${
          expanded ? 'bg-primary/5' : 'bg-card hover:bg-accent/50'
        } ${disabled ? 'opacity-60 cursor-default' : ''}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            aria-hidden="true"
            className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-semibold shrink-0 ${
              complete ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'
            }`}
          >
            {complete ? <Check className="h-3.5 w-3.5" /> : stepIndex}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            <div className="text-xs text-muted-foreground mt-0.5 truncate">
              {disabled ? 'Choose a method first' : summary}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!disabled && needsAttention && (
            <span
              data-testid={`payments-setup-step-${stepKey}-attention`}
              className="text-xs font-medium text-warning bg-warning/10 border border-warning/30 px-2 py-0.5 rounded"
            >
              Needs attention
            </span>
          )}
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
        </div>
      </button>
      {expanded && !disabled && (
        <div className="border-t border-border bg-background p-4">{children}</div>
      )}
    </div>
  );
}

/* ── Summary helpers (small, deliberately local — see PaymentSettingsForm.tsx for the editors
   these mirror; those formatters are not exported and this layer's scope excludes that file) ── */

function describeMethod(method: MethodSelection): string {
  if (method === 'ach') return 'ACH / Bank Transfer';
  if (method === 'external') return 'External (Zelle / Cash)';
  return 'Not set';
}

function describeAccount(
  wantsAch: boolean,
  wantsExternal: boolean,
  paymentConfig: PaymentConfig | null,
  propertyBankInfo: PropertyBankInfo | null,
  fundingSources: LandlordFundingSource[]
): string {
  if (wantsAch) {
    const selected = paymentConfig?.destinationId
      ? fundingSources.find((s) => s.id === paymentConfig.destinationId)
      : null;
    if (selected) return formatAccountSummary(selected);
    if (propertyBankInfo?.defaultDepositAccount) {
      return `Property default: ${formatAccountSummary(propertyBankInfo.defaultDepositAccount)}`;
    }
    return 'Not set';
  }
  if (wantsExternal) {
    const mon = propertyBankInfo?.monitoringAccount;
    if (mon) return `${mon.institutionName} ••••${mon.accountMask}`;
    return 'Not set';
  }
  return 'Not set';
}

function formatAccountSummary(account: { bankName: string; accountMask: string; friendlyName: string | null; isPrimary: boolean }): string {
  const details = `${account.bankName} ••••${account.accountMask}`;
  const label = account.friendlyName ? `${account.friendlyName} (${details})` : details;
  return account.isPrimary ? `${label} (Primary)` : label;
}

function describeLateFee(config: PaymentConfig | null): string {
  if (!config || config.lateFeeType == null) return 'No late fee';
  const amount = config.lateFeeAmount != null ? config.lateFeeAmount / 100 : 0;
  if (config.lateFeeType === 'flat') return `Flat $${amount}`;
  if (config.lateFeeType === 'percentage') return `${amount}% of rent`;
  const max = config.lateFeeMaxAmount != null ? config.lateFeeMaxAmount / 100 : null;
  return `$${amount}/day${max != null ? ` up to $${max}` : ''}`;
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}
